import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  fetchCrmContacts,
  fetchCrmDeals,
  fetchCrmFile,
  fetchCrmQuotes,
  failureCode,
  type CrmContactView,
  type CrmDealView,
  type CrmQuoteView,
} from "../services/ccTenantViewService";
import { boardKpis, buildDealsBoard } from "../../crm/sections/DealsBoard";
import type { Deal } from "../../crm/services/dealsService";
import { Card, Kpi, PageHeader, StateBox, money } from "../components/ui";

// Prospectos de Wayak que lleva Coding Cloud (CC-CONSOLE #1415): negocios,
// cotizaciones y contactos del producto Wayak, leídos EN VIVO de CC-CONSOLE.
// Aquí no se guarda ni se edita nada: lo que entra allá (un formulario, una
// importación de HubSpot, un importe capturado) se ve aquí al recargar.

type Tab = "pipeline" | "deals" | "quotes" | "contacts";
const TABS: Tab[] = ["pipeline", "deals", "quotes", "contacts"];

const day = (iso: string | null) => (iso ? iso.slice(0, 10) : "—");
const has = (q: string, ...values: (string | null | undefined)[]) => !q || values.some((v) => (v ?? "").toLowerCase().includes(q));

const KIND_TONE: Record<string, string> = {
  won: "bg-emerald-500/15 text-emerald-600",
  lost: "bg-red-500/15 text-red-500",
  open: "bg-sky-500/15 text-sky-600",
};

/** «Ver» un adjunto: pide un enlace firmado y lo abre en otra pestaña. */
function FileButton({ id, name, onError }: { id: string | null; name: string | null; onError: () => void }) {
  const { t } = useTranslation("common");
  const [busy, setBusy] = useState(false);
  if (!id) return <span className="text-content-secondary">—</span>;
  return (
    <button
      type="button"
      title={name ?? undefined}
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          const link = await fetchCrmFile(id);
          window.open(link.url, "_blank", "noopener,noreferrer");
        } catch {
          onError();
        } finally {
          setBusy(false);
        }
      }}
      className="inline-flex items-center gap-1 whitespace-nowrap rounded-md px-2 py-1 text-xs font-medium text-primary-500 hover:bg-primary-500/10 disabled:opacity-50"
    >
      <i className={busy ? "ri-loader-4-line animate-spin" : "ri-file-pdf-2-line"} />
      {t("clientView.crm.viewQuote")}
    </button>
  );
}

function Amount({ deal }: { deal: CrmDealView }) {
  const { t } = useTranslation("common");
  return (
    <span title={deal.amountNote ?? undefined} className="tabular-nums whitespace-nowrap">
      {money(deal.amount)}
      {deal.amountKind === "mensual" && <span className="ml-1 text-[10px] text-content-secondary">{t("clientView.crm.perMonth")}</span>}
    </span>
  );
}

export default function CrmClientView() {
  const { t } = useTranslation("common");
  const [tab, setTab] = useState<Tab>("pipeline");
  const [deals, setDeals] = useState<CrmDealView[]>([]);
  const [quotes, setQuotes] = useState<CrmQuoteView[]>([]);
  const [contacts, setContacts] = useState<CrmContactView[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fileError, setFileError] = useState(false);
  const [query, setQuery] = useState("");
  const [pipelineId, setPipelineId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [d, q] = await Promise.all([fetchCrmDeals(), fetchCrmQuotes()]);
      setDeals(d);
      setQuotes(q);
      setContacts(null);
    } catch (err) {
      setError(failureCode(err) ?? "generic");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Los contactos son miles: se piden hasta abrir su pestaña.
  useEffect(() => {
    if (tab !== "contacts" || contacts !== null || loading || error) return;
    let alive = true;
    fetchCrmContacts()
      .then((rows) => alive && setContacts(rows))
      .catch((err) => alive && setError(failureCode(err) ?? "generic"));
    return () => {
      alive = false;
    };
  }, [tab, contacts, loading, error]);

  const q = query.trim().toLowerCase();
  const shownDeals = useMemo(() => deals.filter((d) => has(q, d.title, d.company, d.contact)), [deals, q]);
  const boards = useMemo(() => buildDealsBoard(shownDeals as unknown as Deal[], []), [shownDeals]);
  const board = boards.find((b) => b.id === pipelineId) ?? boards[0] ?? null;
  const kpis = boardKpis(board);
  const shownQuotes = useMemo(() => quotes.filter((x) => has(q, x.title, x.company, x.contact)), [quotes, q]);
  const shownContacts = useMemo(() => (contacts ?? []).filter((c) => has(q, c.name, c.email, c.company)), [contacts, q]);
  const onFileError = () => setFileError(true);

  return (
    <div className="w-full min-w-0 space-y-4 overflow-y-auto p-4 sm:p-6">
      <PageHeader title={t("clientView.crm.title")} subtitle={t("clientView.crm.subtitle")}>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("clientView.crm.search")}
          aria-label={t("clientView.crm.search")}
          className="rounded-lg border border-line bg-card px-3 py-2 text-xs text-content"
        />
        <button type="button" onClick={() => void load()} className="rounded-lg border border-line px-3 py-2 text-xs text-content-secondary hover:bg-subtle">
          <i className="ri-refresh-line" /> {t("clientView.crm.refresh")}
        </button>
      </PageHeader>

      <div className="flex flex-wrap gap-2" role="tablist">
        {TABS.map((id) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            onClick={() => setTab(id)}
            className={`rounded-lg px-3 py-2 text-xs font-medium ${tab === id ? "bg-primary-500 text-on-primary" : "border border-line text-content-secondary hover:bg-subtle"}`}
          >
            {t(`clientView.crm.tabs.${id}`)}
          </button>
        ))}
      </div>

      <StateBox loading={loading} error={error} onRetry={() => void load()} />
      {fileError && (
        <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-500" role="alert">
          {t("clientView.crm.fileError")}
        </p>
      )}

      {!loading && !error && tab === "pipeline" && (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Kpi label={t("clientView.crm.kpis.open")} value={String(kpis.open.count)} />
            <Kpi label={t("clientView.crm.kpis.openValue")} value={money(kpis.open.total)} />
            <Kpi label={t("clientView.crm.kpis.won")} value={String(kpis.won.count)} hint={money(kpis.won.total)} />
            <Kpi label={t("clientView.crm.kpis.lost")} value={String(kpis.lost.count)} hint={money(kpis.lost.total)} />
          </div>
          {boards.length > 1 && (
            <label className="flex items-center gap-2 text-xs text-content-secondary">
              {t("clientView.crm.pipeline")}
              <select value={board?.id ?? ""} onChange={(e) => setPipelineId(e.target.value)} className="rounded-lg border border-line bg-card px-2 py-1 text-content">
                {boards.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.label}
                  </option>
                ))}
              </select>
            </label>
          )}
          {!board || board.columns.length === 0 ? (
            <p className="text-xs text-content-secondary">{t("clientView.empty")}</p>
          ) : (
            <div className="flex gap-3 overflow-x-auto pb-2" data-testid="crm-client-board">
              {board.columns.map((column) => (
                <section key={column.id} className="flex w-64 shrink-0 flex-col rounded-2xl border border-line bg-card" data-testid="crm-client-column">
                  <header className="border-b border-line p-3">
                    <div className="flex items-center justify-between gap-2">
                      <h2 className="truncate text-sm font-semibold text-content" title={column.label}>
                        {column.label}
                      </h2>
                      <span className={`rounded-full px-2 py-0.5 text-[11px] ${KIND_TONE[column.kind]}`}>{column.deals.length}</span>
                    </div>
                    <p className="mt-1 text-xs text-content-secondary tabular-nums">{money(column.total)}</p>
                  </header>
                  <ul className="max-h-[60vh] space-y-2 overflow-y-auto p-2">
                    {column.deals.map((d) => {
                      const deal = d as unknown as CrmDealView;
                      return (
                        <li key={deal.id} className="rounded-xl border border-line bg-subtle p-2 text-xs">
                          <p className="font-medium text-content">{deal.title ?? "—"}</p>
                          <p className="text-content-secondary">{[deal.company, deal.contact].filter(Boolean).join(" · ") || "—"}</p>
                          <div className="mt-1 flex items-center justify-between gap-2">
                            <Amount deal={deal} />
                            <FileButton id={deal.quoteAttachmentId} name={deal.quoteAttachmentName} onError={onFileError} />
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              ))}
            </div>
          )}
        </>
      )}

      {!loading && !error && tab === "deals" && (
        <Card>
          {shownDeals.length === 0 ? (
            <p className="text-xs text-content-secondary">{t("clientView.empty")}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-content-secondary">
                    <th className="py-1">{t("clientView.crm.columns.deal")}</th>
                    <th className="py-1">{t("clientView.crm.columns.company")}</th>
                    <th className="py-1">{t("clientView.crm.columns.contact")}</th>
                    <th className="py-1">{t("clientView.crm.columns.stage")}</th>
                    <th className="py-1 text-right">{t("clientView.crm.columns.amount")}</th>
                    <th className="py-1 text-center">{t("clientView.crm.columns.quote")}</th>
                    <th className="py-1">{t("clientView.crm.columns.created")}</th>
                  </tr>
                </thead>
                <tbody>
                  {shownDeals.map((d) => (
                    <tr key={d.id} className="border-t border-line text-content">
                      <td className="py-1.5">{d.title ?? "—"}</td>
                      <td className="py-1.5">{d.company ?? "—"}</td>
                      <td className="py-1.5">{d.contact ?? "—"}</td>
                      <td className="py-1.5 text-xs">{d.hubspotStageLabel ?? d.stage ?? t("clientView.crm.noStage")}</td>
                      <td className="py-1.5 text-right">
                        <Amount deal={d} />
                      </td>
                      <td className="py-1.5 text-center">
                        <FileButton id={d.quoteAttachmentId} name={d.quoteAttachmentName} onError={onFileError} />
                      </td>
                      <td className="py-1.5 whitespace-nowrap text-xs">{day(d.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {!loading && !error && tab === "quotes" && (
        <Card>
          {shownQuotes.length === 0 ? (
            <p className="text-xs text-content-secondary">{t("clientView.empty")}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-content-secondary">
                    <th className="py-1">{t("clientView.crm.columns.quote")}</th>
                    <th className="py-1">{t("clientView.crm.columns.company")}</th>
                    <th className="py-1">{t("clientView.crm.columns.stage")}</th>
                    <th className="py-1">{t("clientView.crm.columns.status")}</th>
                    <th className="py-1 text-right">{t("clientView.crm.columns.amount")}</th>
                    <th className="py-1">{t("clientView.crm.columns.sent")}</th>
                    <th className="py-1">{t("clientView.crm.columns.validUntil")}</th>
                    <th className="py-1 text-center">{t("clientView.crm.columns.document")}</th>
                  </tr>
                </thead>
                <tbody>
                  {shownQuotes.map((x) => (
                    <tr key={x.id} className="border-t border-line text-content">
                      <td className="py-1.5">{x.title ?? "—"}</td>
                      <td className="py-1.5">{x.company ?? x.contact ?? "—"}</td>
                      <td className="py-1.5 text-xs">{x.dealStageLabel ?? "—"}</td>
                      <td className="py-1.5 text-xs">{x.status ?? "—"}</td>
                      <td className="py-1.5 text-right tabular-nums whitespace-nowrap">{money(x.amount, x.currency)}</td>
                      <td className="py-1.5 whitespace-nowrap text-xs">{day(x.sentAt)}</td>
                      <td className="py-1.5 whitespace-nowrap text-xs">{day(x.expiresAt)}</td>
                      <td className="py-1.5 text-center">
                        {x.attachmentId ? (
                          <FileButton id={x.attachmentId} name={x.attachmentName} onError={onFileError} />
                        ) : x.documentUrl ? (
                          <a href={x.documentUrl} target="_blank" rel="noopener noreferrer" className="text-xs font-medium text-primary-500">
                            {t("clientView.crm.viewQuote")}
                          </a>
                        ) : (
                          <span className="text-content-secondary">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {!loading && !error && tab === "contacts" && (
        <Card>
          {contacts === null ? (
            <p className="text-xs text-content-secondary">{t("clientView.loading")}</p>
          ) : shownContacts.length === 0 ? (
            <p className="text-xs text-content-secondary">{t("clientView.empty")}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-content-secondary">
                    <th className="py-1">{t("clientView.crm.columns.name")}</th>
                    <th className="py-1">{t("clientView.crm.columns.company")}</th>
                    <th className="py-1">{t("clientView.crm.columns.email")}</th>
                    <th className="py-1">{t("clientView.crm.columns.phone")}</th>
                    <th className="py-1">{t("clientView.crm.columns.origin")}</th>
                    <th className="py-1">{t("clientView.crm.columns.created")}</th>
                  </tr>
                </thead>
                <tbody>
                  {shownContacts.slice(0, 500).map((c) => (
                    <tr key={c.id} className="border-t border-line text-content">
                      <td className="py-1.5">{c.name ?? "—"}</td>
                      <td className="py-1.5">{c.company ?? "—"}</td>
                      <td className="py-1.5 text-xs">{c.email ?? "—"}</td>
                      <td className="py-1.5 text-xs">{c.whatsapp ?? "—"}</td>
                      <td className="py-1.5 text-xs">{c.source ?? "—"}</td>
                      <td className="py-1.5 whitespace-nowrap text-xs">{day(c.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {shownContacts.length > 500 && (
                <p className="mt-2 text-xs text-content-secondary">
                  {shownContacts.length.toLocaleString("es-MX")} · 500 ↑ · {t("clientView.crm.search")}
                </p>
              )}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
